import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from '../../../common_components/FormInputs';
import { fetchClientsWithReports, fetchReports, selectClientsWithReports, selectReports, selectClientsWithReportsLoading, selectReportsLoading, } from '../../../redux/metaAdAuditSlice';
import { fDate } from '../utils/formatters';
const ReportSwitcher = ({ clientId, reportId, onSwitchReport, }) => {
    const dispatch = useDispatch();
    const clients = useSelector(selectClientsWithReports);
    const clientReports = useSelector(selectReports);
    const clientsLoading = useSelector(selectClientsWithReportsLoading);
    const reportsLoading = useSelector(selectReportsLoading);
    // Load all clients-with-reports on mount
    useEffect(() => {
        dispatch(fetchClientsWithReports());
    }, [dispatch]);
    // When the selected client changes, fetch that client's report list so
    // we can populate the secondary dropdown.
    useEffect(() => {
        if (!clientId)
            return;
        dispatch(fetchReports({ client_id: clientId, limit: 100 }));
    }, [clientId, dispatch]);
    const clientOptions = useMemo(() => clients.map((c) => ({
        value: String(c._id),
        label: `${c.name}${c.report_count > 1 ? ` (${c.report_count})` : ''}`,
    })), [clients]);
    // Only reports for the selected client
    const reportsForClient = useMemo(() => clientReports.filter((r) => String(r.client_id) === String(clientId)), [clientReports, clientId]);
    const reportOptions = useMemo(() => reportsForClient.map((r) => ({
        value: r.report_id,
        label: `${r.label || r.client_name || 'Untitled Report'} (${fDate(r.period_start)} → ${fDate(r.period_end)})`,
    })), [reportsForClient]);
    const showReportDropdown = reportsForClient.length > 1;
    const handleClientChange = (newClientId) => {
        // Pick the most recently-updated report for this client.
        const picks = clientReports.filter((r) => String(r.client_id) === String(newClientId));
        if (!picks.length) {
            // Reports for that client aren't in memory yet. Fetch then switch.
            dispatch(fetchReports({ client_id: newClientId, limit: 100 }))
                .unwrap()
                .then((data) => {
                const items = data?.items || [];
                if (items.length)
                    onSwitchReport(items[0].report_id);
            })
                .catch(() => {
                /* toast handled upstream */
            });
            return;
        }
        // Assume clientReports is already sorted desc by created_at (backend sort).
        onSwitchReport(picks[0].report_id);
    };
    return (_jsxs(Box, { sx: {
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            width: '100%',
        }, children: [_jsx(Box, { sx: { minWidth: 260 }, children: _jsx(Dropdown, { label: "", value: clientId || '', onChange: (v) => handleClientChange(v), options: clientOptions, placeholder: clientsLoading ? "Loading clients..." : "Select client", loading: clientsLoading, searchable: true, size: "sm" }) }), showReportDropdown && (_jsx(Box, { sx: { minWidth: 340 }, children: _jsx(Dropdown, { label: "", value: reportId || '', onChange: (v) => onSwitchReport(v), options: reportOptions, placeholder: reportsLoading ? "Loading reports..." : "Select report", loading: reportsLoading, size: "sm" }) }))] }));
};
export default ReportSwitcher;
